import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { QUERY_KEYS } from '~/constants/react-query'
import { useActiveOrgId } from '~/hooks/org'
import { CreateTransactionData } from '~/hooks/smart-account-actions/createTransactionData.type'
import { useCreateTransaction } from '~/hooks/smart-account-actions/useCreateTransaction'
import { getSplitsErrorMessage, useServerRequests } from '~/hooks/useRequests'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import { BackendTransactionCall } from '~/types'
import { captureException } from '~/utils/errors'

// Only the fields the server's deposit builder sends.
type PlanDepositTransactionData = Pick<
  CreateTransactionData,
  'name' | 'smartAccountId' | 'chainId' | 'isOfframp' | 'types' | 'memo'
> & {
  calls: Array<Omit<BackendTransactionCall, 'value'> & { value: string }>
}

// Fetches the server-built deposit and opens the transaction review dialog.
// Same flow as useMigrateEarn: the Treasury is the sender.
export const usePlanDeposit = (planId: number | undefined) => {
  const orgId = useActiveOrgId()
  const { smartAccounts } = useSmartAccounts()
  const { serverGetRequest } = useServerRequests()
  const { createTransaction } = useCreateTransaction()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      if (!orgId || planId === undefined) return
      const response = await serverGetRequest<{
        transactionData: PlanDepositTransactionData
      }>({
        path: '/api/org/plan_deposit',
        params: { orgId, planId },
        onError: (error) => {
          captureException(error)
          toast.error(getSplitsErrorMessage(error) ?? 'Failed to build deposit')
        },
      })
      if (!response) return

      const transactionData = response.data.transactionData
      const treasury = smartAccounts.find(
        (account) => account.id === transactionData.smartAccountId,
      )
      if (!treasury) {
        captureException(
          new Error(
            `Plan deposit account ${transactionData.smartAccountId} was not found`,
          ),
        )
        toast.error('Failed to find the deposit account')
        return
      }
      await createTransaction({
        transactionId: undefined,
        dontOpenDialog: false,
        txData: {
          ...transactionData,
          smartAccountAddress: treasury.address,
          calls: transactionData.calls.map((call) => ({
            ...call,
            value: BigInt(call.value),
          })),
          // The balances provider lists new vault positions a few seconds
          // after the deposit lands, and the org's plan is derived from those
          // balances; refresh all three once more after it catches up.
          onConfirmation: () => {
            setTimeout(() => {
              queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.SMART_ACCOUNT_BALANCES],
              })
              queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.ORG_BALANCES, orgId],
              })
              queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.ORG_PLAN, orgId],
              })
            }, 3_000)
          },
        },
      })
    },
    // serverGetRequest toasts its own errors; this catches the rest.
    onError: (error) => {
      captureException(error)
      toast.error('Failed to start the deposit')
    },
  })
}
